import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import authService, { User, isDoctorWithoutPharmacyAccess } from '../services/authService';
import pharmacyPermissionsService from '../services/pharmacyPermissionsService';
import { resetGeminiImportOnLogout } from '../services/excelGeminiImportManager';
import { redirectToLoginAfterLogout } from '../config/publicEnv';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  loginWithToken: (accessToken: string, refreshToken?: string, target?: string) => Promise<void>;
  loginWithLauncherToken: (launcherToken: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => void;
  refreshPermissions: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Initialize auth state
    const initializeAuth = () => {
      const currentUser = authService.getUser();
      if (currentUser && authService.isAuthenticated()) {
        // Drop stale Doctor-only sessions that should not use pharmacy.
        if (isDoctorWithoutPharmacyAccess(currentUser)) {
          void authService.logout();
          setUser(null);
        } else {
          setUser(currentUser);
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  // Start session monitor only after authentication to avoid startup work on login screen.
  useEffect(() => {
    if (!user || !authService.isAuthenticated()) return;
    const intervalId = authService.startSessionMonitor(() => {
      setUser(null);
      alert('Your session has been terminated. Another device has logged in.');
    });
    return () => {
      clearInterval(intervalId);
    };
  }, [user]);

  const login = async (email: string, password: string, rememberMe = false) => {
    try {
      await authService.login({ email, password, rememberMe });
      // Use normalized session user (Admin-first + role priority), not raw API payload.
      const userData = authService.getUser();
      if (userData) {
        setUser(userData);
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      throw error;
    }
  };

  const loginWithToken = async (accessToken: string, refreshToken?: string, target?: string) => {
    const userData = await authService.loginWithToken(accessToken, refreshToken, target);
    setUser(userData);
    navigate('/dashboard', { replace: true });
  };

  const loginWithLauncherToken = async (launcherToken: string) => {
    const userData = await authService.loginWithLauncherToken(launcherToken);
    setUser(userData);
    navigate('/dashboard', { replace: true });
  };

  const logout = async () => {
    resetGeminiImportOnLogout();
    await authService.logout();
    setUser(null);
    redirectToLoginAfterLogout();
  };

  const refreshUser = () => {
    const currentUser = authService.getUser();
    if (currentUser && authService.isAuthenticated()) {
      setUser(currentUser);
    }
  };

  const refreshPermissions = useCallback(async () => {
    if (!authService.isAuthenticated()) return;
    try {
      const res = await pharmacyPermissionsService.getMyPermissions();
      const permissions = res?.data?.permissions ?? [];
      const currentUser = authService.getUser();
      if (currentUser) {
        const updated = { ...currentUser, pharmacyPermissions: permissions };
        sessionStorage.setItem('user', JSON.stringify(updated));
        setUser(updated);
      }
    } catch {
      // Keep existing user on error
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user && authService.isAuthenticated(),
        isLoading,
        login,
        loginWithToken,
        loginWithLauncherToken,
        logout,
        refreshUser,
        refreshPermissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

